import { DateTime } from 'luxon';
import type { JSX } from 'react';

import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { useStore } from 'dg-web-shared/lib/Flux.tsx';
import { DateHeader } from '../../../clearance-permit-list/components/shared/DateHeader.tsx';
import { DatePicker } from '../../../ui/date-picker/DatePicker.tsx';
import { SingleSelection } from '../../../ui/labeled-elements/SingleSelection.tsx';
import {
    HalfSlideIn,
    SecondLevelHeader,
    SlideInBody,
    SlideInHeaderTexts,
} from '../../../ui/slidein/Slidein.tsx';
import { outOfServiceTexts } from '../../i18n/TomTexts.ts';
import * as TomLayoutState from '../../state/TomLayoutState.ts';
import * as TomState from '../../state/TomState.ts';

export function OutOfServiceDateHalfSlideIn() {
    const { storeState, update } = useStore(store => ({
        form: TomState.OutOfService.Form.get(store),
        layout: TomLayoutState.Layout.get(store),
    }));
    const { language } = useLanguage();

    function getMinPossibleDate(): DateTime {
        return DateTime.now().endOf('day');
    }

    function renderValidFrom(): JSX.Element | null {
        const texts = outOfServiceTexts[language];
        if (!storeState.layout) {
            return null;
        }
        const defaultMinDate = getMinPossibleDate();
        return (
            <div>
                <DateHeader
                    label={texts.StartDate()}
                    date={storeState.form.startDate}
                />
                <DatePicker
                    currentDate={defaultMinDate}
                    selectedDate={storeState.form.startDate}
                    minDate={defaultMinDate}
                    onSelect={d =>
                        update(store =>
                            TomState.OutOfService.Form.stateWrite(store, {
                                startDate: d.startOf('day'),
                                endDate:
                                    d <
                                    (storeState.form.endDate ||
                                        DateTime.fromISO('1970-01-01'))
                                        ? storeState.form.endDate
                                        : null,
                            }),
                        )
                    }
                    language={language}
                />
            </div>
        );
    }

    function renderToDatePicker(): JSX.Element {
        const texts = outOfServiceTexts[language];
        const defaultMinDate = getMinPossibleDate();
        return (
            <div style={{ marginTop: 32 }}>
                <DateHeader
                    label={texts.EndDate()}
                    date={storeState.form.endDate}
                />
                <DatePicker
                    currentDate={defaultMinDate}
                    selectedDate={storeState.form.endDate}
                    minDate={storeState.form.startDate || defaultMinDate}
                    onSelect={d =>
                        update(store =>
                            TomState.OutOfService.Form.stateWrite(store, {
                                endDate: d.endOf('day'),
                            }),
                        )
                    }
                    language={language}
                />
            </div>
        );
    }

    const texts = outOfServiceTexts[language];
    return (
        <HalfSlideIn open={storeState.layout.outOfServiceDatePickerOpen}>
            <SecondLevelHeader
                onClose={() =>
                    update(store =>
                        TomLayoutState.Layout.stateWrite(store, {
                            outOfServiceDatePickerOpen: false,
                        }),
                    )
                }
            >
                <SlideInHeaderTexts
                    subtitle={''}
                    title={texts.OutOfServiceDateSlideInTitle()}
                    hasLeftIcon={false}
                />
            </SecondLevelHeader>
            <SlideInBody>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {renderValidFrom()}
                    {renderToDatePicker()}
                </div>
            </SlideInBody>
        </HalfSlideIn>
    );
}

interface OutOfServiceDateSelectionProps {
    isValid: boolean;
    showErrors: boolean;
}

export function OutOfServiceDateSelection({
    isValid,
    showErrors,
}: OutOfServiceDateSelectionProps) {
    const { storeState, update } = useStore(store => ({
        form: TomState.OutOfService.Form.get(store),
        layout: TomLayoutState.Layout.get(store),
    }));
    const { language } = useLanguage();

    const texts = outOfServiceTexts[language];
    const selection = Formatter.openRange(
        storeState.form.startDate,
        storeState.form.endDate,
        d => Formatter.dayMonthYear(d),
    );
    const write = (s: Partial<TomLayoutState.Layout.State>) =>
        update(store => TomLayoutState.Layout.stateWrite(store, s));

    return (
        <div>
            <SingleSelection
                label={texts.OutOfServiceDateSlideInTitle()}
                focused={false}
                selection={selection}
                onClick={() => write({ outOfServiceDatePickerOpen: true })}
                errorText={
                    showErrors && !isValid
                        ? texts.MessageDateConstraints()
                        : undefined
                }
            />
        </div>
    );
}
